import { type StateListItem, type StateListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { StateFormDrawer } from '../components/StateFormDrawer';
import { listStates } from '../states.api';
import {
  useBulkDeleteStates,
  useBulkSetStatesStatus,
  useCountryOptions,
  useDeleteState,
  useStatesList,
} from '../use-states';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const MIN_SEARCH_LENGTH = 3;

export const StatesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteState = useDeleteState();
  const bulkDeleteMut = useBulkDeleteStates();
  const bulkSetStatusMut = useBulkSetStatesStatus();
  const countriesQuery = useCountryOptions();

  const countryOptions = useMemo(
    () => [
      { value: '', label: 'All countries' },
      ...(countriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [countriesQuery.data],
  );

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      { id: 'country', label: 'Country', options: countryOptions, widthClass: 'w-56' },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-48',
      },
    ],
    [countryOptions],
  );

  const columns: ListScreenColumns<StateListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<StateListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'State Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            {row.isoCode && <span className="text-xs text-zinc-500">{row.isoCode}</span>}
          </div>
        ),
      },
      {
        id: 'country',
        header: 'Country',
        cell: (row) => (
          <span>
            {row.country.name} ({row.country.isoCode.toUpperCase()})
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<StateListItem, StateListQuery>
      title="States"
      description="States and provinces, each belonging to a country."
      useList={useStatesList}
      defaultSort={{ by: 'name', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const country = state.filters.country;
        const status = state.filters.status;
        return {
          ...base,
          ...(country ? { countryId: country } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add state' }}
      renderDrawer={({ open, mode, row, close }) => (
        <StateFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          state={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} state${rows.length === 1 ? '' : 's'}?`,
            description: 'These states will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} state${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in geography pickers.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listStates(q),
        filename: 'states',
        csv: [
          { header: 'State Name', value: (r) => r.name },
          { header: 'ISO Code', value: (r) => r.isoCode ?? '' },
          { header: 'Country', value: (r) => r.country.name },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteState.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this state?',
            description: `${row.name} (${row.country.name}) will be soft-deleted and hidden from lists.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name or ISO code (min 3 characters)',
        emptyTitle: 'No states match these filters',
        emptyDescription: 'Adjust the country or status filter, or clear the search box.',
        errorTitle: 'Failed to load states',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
